import React, { useState, useEffect, useRef } from 'react';
import { useSupabase } from '../../supabaseAuth';

// Simple loading spinner component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Admin Auth Component - Uses existing Supabase authentication
const AdminAuth = ({ children }) => {
  const { user, session, loading } = useSupabase();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [adminError, setAdminError] = useState(null);
  
  // Use refs to track if we've already checked admin status
  const hasCheckedAdmin = useRef(false);
  const lastCheckedUserId = useRef(null);

  // Check if user is admin by making a test API call
  const checkAdminStatus = async () => {
    if (!user || !session) {
      setIsAdmin(false);
      setAdminLoading(false);
      hasCheckedAdmin.current = false;
      lastCheckedUserId.current = null;
      return;
    }

    // Skip if we've already checked for this user
    if (hasCheckedAdmin.current && lastCheckedUserId.current === user.id) {
      // console.log('Admin status already checked for this user, skipping');
      setAdminLoading(false);
      return;
    }

    try {
      setAdminLoading(true);
      setAdminError(null);

      // Get API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
      
      // Get access token
      const token = session?.access_token;
      if (!token) {
        // console.log('No access token available');
        setIsAdmin(false);
        setAdminLoading(false);
        return;
      }

      // console.log('Checking admin status for user:', user.id);
      
      // Make a test call to admin endpoint
      const response = await fetch(`${apiBaseUrl}/api/admin/audit-logs?limit=1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // console.log('Admin check response status:', response.status);

      if (response.status === 200) {
        // User is admin
        setIsAdmin(true);
        hasCheckedAdmin.current = true;
        lastCheckedUserId.current = user.id;
        // console.log('User confirmed as admin');
      } else if (response.status === 404) {
        // User is not admin (404 returned for non-admin users)
        setIsAdmin(false);
        hasCheckedAdmin.current = true;
        lastCheckedUserId.current = user.id;
        // console.log('User is not admin (404 response)');
      } else {
        // Other error
        // console.log('Admin check failed with status:', response.status);
        const errorText = await response.text();
        // console.log('Error response:', errorText);
        setAdminError(`Admin check failed: ${response.status}`);
        setIsAdmin(false);
        hasCheckedAdmin.current = false;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setAdminError('Failed to verify admin status');
      setIsAdmin(false);
      hasCheckedAdmin.current = false;
    } finally {
      setAdminLoading(false);
    }
  };

  // Check admin status only when user changes (not session changes)
  useEffect(() => {
    if (!loading && user) {
      // Only check if user ID has changed or we haven't checked yet
      if (!hasCheckedAdmin.current || lastCheckedUserId.current !== user.id) {
        checkAdminStatus();
      } else {
        // We've already checked for this user, just set loading to false
        setAdminLoading(false);
      }
    } else if (!loading && !user) {
      // User logged out
      setIsAdmin(false);
      setAdminLoading(false);
      hasCheckedAdmin.current = false;
      lastCheckedUserId.current = null;
    }
  }, [user?.id, loading]); // Only depend on user.id and loading, not session

  // Show loading spinner while checking authentication
  if (loading || adminLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-3 text-muted">Verifying access...</p>
        </div>
      </div>
    );
  }

  // User not logged in to main app - show standard 404 page
  if (!user || !session) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="mb-4">
            <h1 className="display-1 fw-bold text-muted">404</h1>
            <h2 className="h3 mb-3">Page Not Found</h2>
            <p className="text-muted mb-4">
              The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
            </p>
            <a href="/" className="btn btn-primary">
              <i className="bi bi-house me-2"></i>
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Error checking admin status OR user is not admin - show standard 404 page
  if (adminError || !isAdmin) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="mb-4">
            <h1 className="display-1 fw-bold text-muted">404</h1>
            <h2 className="h3 mb-3">Page Not Found</h2>
            <p className="text-muted mb-4">
              The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
            </p>
            <a href="/" className="btn btn-primary">
              <i className="bi bi-house me-2"></i>
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }


  // User is admin - show admin interface
  return children;
};

// Legacy compatibility exports for existing code
export const AdminAuthProvider = ({ children }) => {
  // This is now just a pass-through since we use Supabase auth directly
  return children;
};

export const AdminProtectedRoute = ({ children }) => {
  // This is now just AdminAuth
  return <AdminAuth>{children}</AdminAuth>;
};

export const useAdminAuth = () => {
  // Mock the old admin auth hook for compatibility
  const { user } = useSupabase();
  
  return {
    isAuthenticated: !!user,
    logout: () => {
      // Redirect to main app logout
      window.location.href = '/logout';
    }
  };
};

// Export as default
export default AdminAuth;

// Also export as named export for compatibility
export { AdminAuth };
